import { useLocation, useNavigate } from 'react-router-dom';

/**
 * BACK1 (his ask, the 152nd, 2026-10-19): "can we make it so it can just go back to previous page so its easy for me
 * or user?" The Apex home-screen app on his iPhone has no browser Back, so after To-dos -> "Draft message" the only
 * way back was the menu. This arrow, in the phone top bar, does what a browser's Back does.
 *
 * ⭐ It shows only when there is an earlier page INSIDE the app: react-router's BrowserRouter numbers every entry it
 *    makes (`window.history.state.idx`, 0 for the first). So it never offers to leave the app, and a launch redirect
 *    (`/` -> `/dashboard`, a replace) still reads 0 - a check on `location.key` would show the arrow there.
 *    ⚠️ `idx` is the router's own bookkeeping, not a documented API: BackButton.test.tsx runs the REAL BrowserRouter,
 *    so a router upgrade that stops writing it turns that file red instead of hiding the arrow.
 */
export function canGoBackInApp(): boolean {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' && idx > 0;
}

const BackChevron = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

export default function BackButton({ className = '' }: { className?: string }) {
  // Subscribing to the location re-renders this on every navigation, so the arrow appears and disappears with it.
  // ⚠️ Measured (the sabotage, react-router 7.9.6): `useNavigate` already subscribes - it calls `useLocation` itself, so
  // removing this line changes nothing today. Kept so the re-render does not rest on another hook's internals.
  useLocation();
  const navigate = useNavigate();
  if (!canGoBackInApp()) return null;
  return (
    <button
      type="button"
      onClick={() => navigate(-1)}
      aria-label="Go back"
      title="Back"
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-900 hover:bg-gray-100 ${className}`}
    >
      <BackChevron />
    </button>
  );
}
